import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ring, Roll, dollars, money, pct0, paceTone } from './Ring';
import { Periods } from './PeriodsFeed';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* Simple SVG bar chart; `ref` draws a faint reference bar behind each item. */
export function Bars({ items, refValue, color = '#0a84ff', testid }) {
  const W = 640, H = 220, pad = { l: 52, r: 8, t: 18, b: 30 }, iw = W - pad.l - pad.r, ih = H - pad.t - pad.b;
  const n = Math.max(1, items.length), bw = Math.min(56, (iw / n) * 0.62);
  const max = Math.max(1, refValue || 0, ...items.map((i) => i.v || 0)) * 1.1;
  const y = (v) => pad.t + ih - (v / max) * ih;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="gpj-bars" role="img" data-testid={testid}>
      {[0, 0.5, 1].map((t) => { const v = t * max; return <g key={t}><line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} style={{ stroke: 'rgba(var(--ink),.07)' }} /><text x={pad.l - 8} y={y(v) + 4} textAnchor="end">{money(v, 0)}</text></g>; })}
      {items.map((it, i) => {
        const cx = pad.l + (iw * (i + 0.5)) / n, h = Math.max(it.v > 0 ? 3 : 0, (it.v / max) * ih);
        return (
          <g key={i}>
            {refValue > 0 && <rect x={cx - bw / 2} y={y(refValue)} width={bw} height={pad.t + ih - y(refValue)} rx={6} style={{ fill: 'rgba(var(--ink),.06)' }} />}
            <rect x={cx - bw / 2} y={pad.t + ih - h} width={bw} height={h} rx={6} fill={it.c || color} opacity={it.dim ? 0.35 : 1} style={{ transition: 'height 1.2s cubic-bezier(.2,.9,.15,1), y 1.2s cubic-bezier(.2,.9,.15,1)' }} />
            {it.v > 0 && n <= 8 && <text className="lbl" x={cx} y={pad.t + ih - h - 6} textAnchor="middle">{money(it.v)}</text>}
            <text className="ax" x={cx} y={H - 10} textAnchor="middle">{it.l}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* Settlement target + pace, settled by week, booked. Reused on Performance and above Settlements. */
export function SettlementPanels({ live }) {
  const st = live?.settled || {};
  const bd = live?.business_days || {};
  const sp = st.target ? (st.amount || 0) / st.target : 0;
  const pr = st.remaining ? (st.actual_per_bd || 0) / (st.needed_per_bd || 1) : 1;
  const weekShare = (st.target || 0) / 4;
  return (
    <div className="gpj-grid gpj-two" data-testid="gpj-settlement-panels">
      <section className="gpj-panel">
        <div className="gpj-panel-h"><h2>Settlement target</h2><p>Pace is spread over business days left</p></div>
        <div className="gpj-hero-main">
          <Ring p={sp} size={170} value={pct0(sp)} of={`${money(st.amount)} of ${money(st.target, 0)}`} tone={st.target ? paceTone(sp, live?.elapsed || 0) : 'green'} label="Settled vs target"
            sub={st.target ? (st.remaining ? `<b>${money(st.remaining)}</b> to settle` : '<b>Target reached</b>') : 'Set a settlement target in Set Goals'} testid="gpj-ring-settle-target" />
          <div className="gpj-facts">
            <div className="gpj-fact"><span>Settled</span><strong><Roll value={dollars(st.amount)} /></strong></div>
            <div className={`gpj-fact remain${st.target && !st.remaining ? ' done' : ''}`}><span>Still to settle</span><strong><Roll value={dollars(st.remaining)} /></strong></div>
            <div className="gpj-fact"><span>Needed per business day</span><strong><Roll value={money(st.needed_per_bd)} /></strong></div>
            <div className="gpj-fact"><span>Average so far per business day</span><strong><Roll value={money(st.actual_per_bd)} /></strong></div>
            <div className="gpj-fact"><span>Booked to settle this month</span><strong><Roll value={dollars(st.booked)} /> <em style={{ fontStyle: 'normal', fontSize: 12, color: 'var(--fg-3)' }}>· {st.booked_count || 0} files</em></strong></div>
            <div className="gpj-fact"><span>Business days left</span><strong>{bd.left ?? '–'} of {bd.total ?? '–'}</strong></div>
          </div>
        </div>
      </section>
      <section className="gpj-panel">
        <div className="gpj-panel-h"><h2>Settled by week</h2><p>Against an even weekly share of target</p></div>
        <Bars color="#30d158" refValue={weekShare} testid="gpj-settled-bars" items={[1, 2, 3, 4].map((w) => ({ l: `Week ${w}`, v: st.by_week?.[w]?.amount || 0 }))} />
        <div className="gpj-legend"><span><i style={{ background: 'var(--green)' }} />Settled</span><span><i style={{ background: 'rgba(var(--ink),.2)' }} />Weekly share of target</span></div>
        <div className="gpj-hero-main" style={{ marginTop: 18 }}>
          <Ring p={pr} size={120} value={money(st.actual_per_bd)} of="a day so far" tone={pr >= 1 ? 'green' : pr >= 0.85 ? 'blue' : 'amber'} label="Daily settlement pace" sub={`Need <b>${money(st.needed_per_bd)}</b> a day`} testid="gpj-ring-pace" />
          <div className="gpj-facts">
            {[1, 2, 3, 4].map((w) => <div className="gpj-fact" key={w}><span>Week {w}</span><strong>{dollars(st.by_week?.[w]?.amount || 0)} <em style={{ fontStyle: 'normal', fontSize: 12, color: 'var(--fg-3)' }}>· {st.by_week?.[w]?.count || 0} files</em></strong></div>)}
          </div>
        </div>
      </section>
    </div>
  );
}

/* Week 1–4 against each week's share of the monthly goals. Portal targets come from the Set Goals scoreboard. */
export function WeeklyGamePlanTable({ live, boardData, authToken, month }) {
  const [sb, setSb] = useState({});
  useEffect(() => {
    if (!authToken || !month) return undefined;
    let on = true;
    axios.get(`${API_BASE}/api/gameplan/activity/scoreboard/${month}?scope=company`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => { if (!on) return; const m = {}; (r.data?.stages || []).forEach((s) => { m[s.key] = s; }); setSb(m); }).catch(() => {});
    return () => { on = false; };
  }, [authToken, month]);
  const weeks = live?.weeks || [];
  const wkTarget = Number(boardData?.weekly_lodgement_target || 0);
  const wkShare = Number(boardData?.monthly_target_amount || 0) / 4;
  const apprWk = Math.ceil((live?.approvals_target || 0) / 4);
  const FOCUS = { 1: 'File preparation', 2: 'Lodgement · assessment', 3: 'Assessment · MIRs', 4: 'Approval · settlement' };
  const cell = (v, t) => <span className={t ? (v >= t ? 'ok' : v >= t * 0.6 ? 'mid' : 'bad') : ''}>{v}{t ? <small> / {t}</small> : null}</span>;
  return (
    <section className="gpj-panel" data-testid="gpj-weekly-table">
      <div className="gpj-panel-h"><h2>Weekly Game Plan</h2><p>Each week against its share of the monthly goals</p></div>
      <div className="gpj-reg-wrap">
        <table className="gpj-reg gpj-week-table">
          <thead><tr><th>Week</th><th>Focus</th><th className="r">Lodged</th><th className="r">Files</th><th className="r">Lodgement target</th><th className="r">Approved</th><th className="r">Portal invites</th><th className="r">Completions</th></tr></thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week} className={w.current ? 'cur' : ''} data-testid={`gpj-week-row-${w.week}`}>
                <td><strong>Week {w.week}</strong><small>{w.range}</small></td>
                <td>{FOCUS[w.week]}</td>
                <td className="r">{dollars(w.lodged)}</td>
                <td className="r">{w.files}</td>
                <td className="r">{wkTarget ? `${wkTarget} files` : '–'}{wkShare ? <small> · {money(wkShare)}</small> : null}</td>
                <td className="r">{cell(w.approved, apprWk)}</td>
                <td className="r">{cell(w.portal_invites, sb.portals_sent?.target_weekly)}</td>
                <td className="r">{cell(w.portal_completions, sb.portals?.target_weekly)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function PerformanceTab({ live, boardData, authToken, month }) {
  const target = Number(boardData?.monthly_target_amount || 0);
  const lodged = Number(boardData?.monthly_lodged_amount || 0);
  const p = target ? lodged / target : 0;
  const bd = live?.business_days || {};
  const dailyShare = target && bd.total ? target / bd.total : 0;
  const days = live?.daily_lodged || [];
  const st = live?.settled || {};
  const pr = st.remaining ? (st.actual_per_bd || 0) / (st.needed_per_bd || 1) : 1;
  return (
    <div className="gpj-grid" data-testid="gpj-performance-tab">
      <section className="gpj-panel">
        <div className="gpj-panel-h"><h2>Daily lodgements this month</h2><p>Each bar is one business day, against the daily share of target</p></div>
        <Bars refValue={dailyShare} testid="gpj-daily-bars" items={days.map((d) => ({ l: String(d.day), v: d.amount, dim: d.future, c: d.weekend ? '#64d2ff' : '#0a84ff' }))} />
      </section>
      <div className="gpj-grid gpj-two">
        <section className="gpj-panel">
          <div className="gpj-panel-h"><h2>Pace</h2><p>Actual against required</p></div>
          <div className="gpj-minis" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))' }}>
            <Ring p={pr} size={160} value={money(st.actual_per_bd)} of="a day so far" tone={pr >= 1 ? 'green' : pr >= 0.85 ? 'blue' : 'amber'} label="Daily settlement pace" sub={`Need <b>${money(st.needed_per_bd)}</b> a day`} testid="gpj-ring-perf-pace" />
            <Ring p={p} size={160} value={pct0(p)} of={`${money(lodged)} of ${money(target, 0)}`} tone={target ? paceTone(p, live?.elapsed || 0) : 'green'} label="Lodged vs target" sub={target ? `<b>${money(Math.max(0, target - lodged))}</b> to go` : 'Set a target'} testid="gpj-ring-perf-lodged" />
          </div>
        </section>
        <Periods live={live} />
      </div>
      <SettlementPanels live={live} />
      <WeeklyGamePlanTable live={live} boardData={boardData} authToken={authToken} month={month} />
    </div>
  );
}
